// Feature: net-worth-dashboard
// Aggregation hook that fetches all asset and liability data in parallel,
// converts each record to the display currency, and returns structured totals.

import { useMemo } from 'react';
import { useFetchAccounts } from './accounts/useAccounts.ts';
import { useInvestments } from './useInvestments';
import { useLiabilities } from './useLiabilities';
import { useFixedAssets } from './useFixedAssets';
import { useFetchEquitySecurities } from '@equitySecurityHooks';
import { useFetchDebtSecurities } from './securities/debt/useDebtSecurities.ts';
import { useFetchDerivativeSecurities } from './securities/derivatives/useDerivativeSecurities.ts';
import { useFetchETFHoldings } from './securities/etf/useETFHoldings.ts';
import { useFetchIndexFundHoldings } from '@/hooks/securities/indexfunds/useIndexFundHoldings.ts';
import { useFetchReitHoldings } from './reits';
import { useFetchCryptoHoldings } from './crypto';
import { useExchangeRates } from './useExchangeRates';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { safeConvertCurrency } from '@/lib/currency.utils';

// ─── Public interfaces ────────────────────────────────────────────────────────

export interface AssetCategory {
  label: string;
  total: number; // already converted to displayCurrency
}

export interface LiabilityCategory {
  label: string;
  total: number; // already converted to displayCurrency
}

export interface NetWorthData {
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
  assetCategories: AssetCategory[];
  liabilityCategories: LiabilityCategory[];
  isLoading: boolean;
  errors: Record<string, Error | null>;
}

// ─── Asset category labels (in display order) ─────────────────────────────────

export const ASSET_CATEGORY_LABELS = [
  'Accounts',
  'Money Market Funds',
  'Fixed Income Funds',
  'Balanced Funds',
  'Mutual Funds',
  'Special Funds',
  'Pension Plans',
  'Equity Securities',
  'Debt Securities',
  'Derivatives',
  'ETFs',
  'Index Funds',
  'REITs',
  'SACCOs',
  'Crypto',
  'Fixed Assets',
] as const;

export const LIABILITY_CATEGORY_LABELS = [
  'Short-Term Loans',
  'Long-Term Loans',
] as const;

// Investment type name → asset category label mapping
const INVESTMENT_TYPE_TO_CATEGORY: Record<string, string> = {
  'Money Market Fund': 'Money Market Funds',
  'Fixed Income Fund': 'Fixed Income Funds',
  'Balanced Fund': 'Balanced Funds',
  'Mutual Fund': 'Mutual Funds',
  'Special Fund': 'Special Funds',
  // Pension types
  'Government Pension Plan': 'Pension Plans',
  'Occupational Pension Plan': 'Pension Plans',
  'Individual Pension Plan': 'Pension Plans',
  'Pension Plan': 'Pension Plans',
};

// ─── SACCO shares query (all user shares, no membershipId required) ───────────

interface SaccoShareRow {
  units: number;
  price_per_unit: number;
}

function useSaccoSharesAll() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['sacco_shares_all', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sacco_shares')
        .select('units, price_per_unit')
        .eq('user_id', user!.id);
      if (error) throw error;
      return data as SaccoShareRow[];
    },
    enabled: !!user,
  });
}

// ─── Main hook ────────────────────────────────────────────────────────────────

export function useNetWorthData(displayCurrency: string): NetWorthData {
  // ── Domain data hooks ──────────────────────────────────────────────────────
  const accountsQuery = useFetchAccounts();
  const investmentsQuery = useInvestments();
  const liabilitiesQuery = useLiabilities();
  const fixedAssetsQuery = useFixedAssets();
  const equitySecuritiesQuery = useFetchEquitySecurities();
  const debtSecuritiesQuery = useFetchDebtSecurities();
  const derivativeSecuritiesQuery = useFetchDerivativeSecurities();
  const etfHoldingsQuery = useFetchETFHoldings();
  const indexFundHoldingsQuery = useFetchIndexFundHoldings();
  const reitHoldingsQuery = useFetchReitHoldings();
  const cryptoHoldingsQuery = useFetchCryptoHoldings();
  const saccoSharesQuery = useSaccoSharesAll();

  // ── Exchange rates ─────────────────────────────────────────────────────────
  const { convert, isLoading: ratesLoading, error: ratesError } = useExchangeRates(displayCurrency);

  // ── Loading state ──────────────────────────────────────────────────────────
  const isLoading =
    ratesLoading ||
    accountsQuery.isLoading ||
    investmentsQuery.isLoading ||
    liabilitiesQuery.isLoading ||
    fixedAssetsQuery.isLoading ||
    equitySecuritiesQuery.isLoading ||
    debtSecuritiesQuery.isLoading ||
    derivativeSecuritiesQuery.isLoading ||
    etfHoldingsQuery.isLoading ||
    indexFundHoldingsQuery.isLoading ||
    reitHoldingsQuery.isLoading ||
    cryptoHoldingsQuery.isLoading ||
    saccoSharesQuery.isLoading;

  // ── Error map ──────────────────────────────────────────────────────────────
  const errors: Record<string, Error | null> = {
    exchangeRates: (ratesError as Error | null) ?? null,
    accounts: (accountsQuery.error as Error | null) ?? null,
    investments: (investmentsQuery.error as Error | null) ?? null,
    liabilities: (liabilitiesQuery.error as Error | null) ?? null,
    fixedAssets: (fixedAssetsQuery.error as Error | null) ?? null,
    equitySecurities: (equitySecuritiesQuery.error as Error | null) ?? null,
    debtSecurities: (debtSecuritiesQuery.error as Error | null) ?? null,
    derivativeSecurities: (derivativeSecuritiesQuery.error as Error | null) ?? null,
    etfHoldings: (etfHoldingsQuery.error as Error | null) ?? null,
    indexFundHoldings: (indexFundHoldingsQuery.error as Error | null) ?? null,
    reitHoldings: (reitHoldingsQuery.error as Error | null) ?? null,
    cryptoHoldings: (cryptoHoldingsQuery.error as Error | null) ?? null,
    saccoShares: (saccoSharesQuery.error as Error | null) ?? null,
  };

  // ── Aggregation (memoized) ─────────────────────────────────────────────────
  const aggregated = useMemo(() => {
    // Initialise all category totals to 0
    const categoryTotals: Record<string, number> = {};
    for (const label of ASSET_CATEGORY_LABELS) {
      categoryTotals[label] = 0;
    }
    const liabilityTotals: Record<string, number> = {
      'Short-Term Loans': 0,
      'Long-Term Loans': 0,
    };

    // ── Accounts ─────────────────────────────────────────────────────────────
    for (const account of accountsQuery.data ?? []) {
      categoryTotals['Accounts'] += safeConvertCurrency(
        account.balance,
        account.currency,
        displayCurrency,
        convert
      );
    }

    // ── Investment accounts (by type) ─────────────────────────────────────────
    for (const inv of investmentsQuery.data ?? []) {
      const typeName = inv.investment_type?.name ?? '';
      const categoryLabel = INVESTMENT_TYPE_TO_CATEGORY[typeName];
      if (categoryLabel && categoryLabel in categoryTotals) {
        categoryTotals[categoryLabel] += safeConvertCurrency(
          inv.balance,
          inv.currency,
          displayCurrency,
          convert
        );
      }
    }

    // ── Equity Securities ─────────────────────────────────────────────────────
    for (const sec of equitySecuritiesQuery.data ?? []) {
      const value = sec.shares_owned * sec.current_price;
      categoryTotals['Equity Securities'] += safeConvertCurrency(
        value,
        sec.currency,
        displayCurrency,
        convert
      );
    }

    // ── Debt Securities ───────────────────────────────────────────────────────
    for (const sec of debtSecuritiesQuery.data ?? []) {
      const value = sec.quantity * sec.nominal_price;
      categoryTotals['Debt Securities'] += safeConvertCurrency(
        value,
        sec.currency,
        displayCurrency,
        convert
      );
    }

    // ── Derivative Securities ─────────────────────────────────────────────────
    for (const sec of derivativeSecuritiesQuery.data ?? []) {
      // Use premium_paid for options, margin_requirement for futures
      const value =
        sec.derivative_type === 'option'
          ? (sec.premium_paid ?? 0) * sec.contract_size
          : (sec.margin_requirement ?? 0) * sec.contract_size;
      categoryTotals['Derivatives'] += safeConvertCurrency(
        value,
        sec.currency,
        displayCurrency,
        convert
      );
    }

    // ── ETF Holdings ──────────────────────────────────────────────────────────
    for (const etf of etfHoldingsQuery.data ?? []) {
      const value = etf.units_held * etf.average_cost_per_unit;
      categoryTotals['ETFs'] += safeConvertCurrency(
        value,
        etf.currency,
        displayCurrency,
        convert
      );
    }

    // ── Index Fund Holdings ───────────────────────────────────────────────────
    for (const fund of indexFundHoldingsQuery.data ?? []) {
      const value = fund.units_held * fund.average_cost_per_unit;
      categoryTotals['Index Funds'] += safeConvertCurrency(
        value,
        fund.currency,
        displayCurrency,
        convert
      );
    }

    // ── REIT Holdings ─────────────────────────────────────────────────────────
    for (const reit of reitHoldingsQuery.data ?? []) {
      const value = reit.units_held * reit.current_price_per_unit;
      categoryTotals['REITs'] += safeConvertCurrency(
        value,
        reit.currency,
        displayCurrency,
        convert
      );
    }

    // ── SACCO Shares (no currency field — treated as display currency) ─────────
    for (const share of saccoSharesQuery.data ?? []) {
      categoryTotals['SACCOs'] += share.units * share.price_per_unit;
    }

    // ── Crypto Holdings ───────────────────────────────────────────────────────
    for (const holding of cryptoHoldingsQuery.data ?? []) {
      categoryTotals['Crypto'] += safeConvertCurrency(
        holding.cost_basis,
        holding.currency,
        displayCurrency,
        convert
      );
    }

    // ── Fixed Assets ──────────────────────────────────────────────────────────
    for (const asset of fixedAssetsQuery.data ?? []) {
      categoryTotals['Fixed Assets'] += safeConvertCurrency(
        asset.value,
        asset.currency,
        displayCurrency,
        convert
      );
    }

    // ── Liabilities ───────────────────────────────────────────────────────────
    for (const liability of liabilitiesQuery.data ?? []) {
      const categoryLabel =
        liability.type === 'short_term' ? 'Short-Term Loans' : 'Long-Term Loans';
      liabilityTotals[categoryLabel] += safeConvertCurrency(
        liability.remaining_balance,
        liability.currency,
        displayCurrency,
        convert
      );
    }

    // ── Build output arrays ───────────────────────────────────────────────────
    const assetCategories: AssetCategory[] = ASSET_CATEGORY_LABELS.map((label) => ({
      label,
      total: categoryTotals[label],
    }));

    const liabilityCategories: LiabilityCategory[] = LIABILITY_CATEGORY_LABELS.map((label) => ({
      label,
      total: liabilityTotals[label],
    }));

    const totalAssets = assetCategories.reduce((sum, c) => sum + c.total, 0);
    const totalLiabilities = liabilityCategories.reduce((sum, c) => sum + c.total, 0);
    const netWorth = totalAssets - totalLiabilities;

    return { assetCategories, liabilityCategories, totalAssets, totalLiabilities, netWorth };
  }, [
    displayCurrency,
    convert,
    accountsQuery.data,
    investmentsQuery.data,
    liabilitiesQuery.data,
    fixedAssetsQuery.data,
    equitySecuritiesQuery.data,
    debtSecuritiesQuery.data,
    derivativeSecuritiesQuery.data,
    etfHoldingsQuery.data,
    indexFundHoldingsQuery.data,
    reitHoldingsQuery.data,
    cryptoHoldingsQuery.data,
    saccoSharesQuery.data,
  ]);

  return {
    ...aggregated,
    isLoading,
    errors,
  };
}
